import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageHeader from "@/components/site/PageHeader";
import { useSEO } from "@/lib/seo";
import { Shield, Landmark, Check, ArrowRight, Info } from "lucide-react";

const RATTSSKYDD_POINTS = [
    "Ingår oftast i din hem-, villa-, bilförsäkring eller företagsförsäkring.",
    "Täcker vanligtvis 75–80 % av ombuds- och rättegångskostnaderna.",
    "Har ett tak (ofta 200 000–300 000 kr) samt en självrisk (typiskt 20–25 %).",
    "Gäller för vissa typer av tvister — kontrollera alltid villkoren i ditt försäkringsbrev.",
];

const RATTSHJALP_POINTS = [
    "Statligt bidrag för juridiska kostnader — administreras av Rättshjälpsmyndigheten.",
    "Aktuellt när du saknar rättsskydd, eller när rättsskyddet inte räcker.",
    "Beviljas utifrån ekonomiskt underlag (bruttoinkomst upp till ca 260 000 kr/år).",
    "Egen avgift (rättshjälpsavgift) utgår med 2–40 % av kostnaderna beroende på inkomst.",
];

const STEG = [
    {
        n: "01",
        title: "Kontakta oss",
        body: "Beskriv ditt ärende via formulär, mejl eller telefon. Det första samtalet är alltid kostnadsfritt.",
    },
    {
        n: "02",
        title: "Vi utreder rätten till ersättning",
        body: "Vi granskar ditt försäkringsbrev och bedömer om rättsskydd gäller, alternativt om rättshjälp är möjlig.",
    },
    {
        n: "03",
        title: "Ansökan & hantering",
        body: "Vi ansöker om rättsskydd hos ditt försäkringsbolag eller om rättshjälp hos Rättshjälpsmyndigheten — och sköter all administration.",
    },
    {
        n: "04",
        title: "Vi driver ditt ärende",
        body: "Med ersättningen beviljad kan vi företräda dig fullt ut. Du betalar endast självrisk eller rättshjälpsavgift enligt villkoren.",
    },
];

export default function Rattshjalp() {
    useSEO({
        title: "Rättshjälp & Rättsskydd",
        description:
            "BM Juridik förmedlar rättshjälp och rättsskydd. Vi förklarar skillnaden och hjälper dig att ansöka så att du får den ekonomiska hjälp du har rätt till.",
    });

    return (
        <div data-testid="page-rattshjalp">
            <PageHeader
                kicker="Rättshjälp & Rättsskydd"
                title="Ekonomisk hjälp"
                accent="när juridiken kostar."
                description="Juridiska kostnader ska inte vara ett hinder för att få sin sak prövad. BM Juridik förmedlar både rättsskydd och rättshjälp — vi hjälper dig att ansöka om den ersättning du har rätt till och sköter hela processen."
                image="https://images.unsplash.com/photo-1747696766706-5485b39bf358"
            />

            <section className="py-20 bg-[#0A0A0B]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7 }}
                            data-testid="rattsskydd-card"
                            className="bg-[#111113] border border-white/5 p-10 rounded-sm"
                        >
                            <div className="w-12 h-12 border border-[#C4A977]/40 grid place-items-center text-[#C4A977]">
                                <Shield size={20} />
                            </div>
                            <p className="mt-6 overline">Rättsskydd</p>
                            <h2 className="mt-3 font-serif text-3xl lg:text-4xl tracking-tight leading-tight">
                                Ersättning via din försäkring.
                            </h2>
                            <p className="mt-5 text-[#A1A1AA] leading-relaxed">
                                Rättsskydd är ett moment som ingår i de flesta
                                privat- och företagsförsäkringar. Det täcker
                                merparten av kostnaderna för juridiskt ombud
                                vid tvister — till exempel vid en vårdnadstvist,
                                ett fastighetsköp som gått snett, eller en
                                anställningsfråga. Försäkringsbolaget betalar
                                direkt till oss; du betalar endast självrisk.
                            </p>
                            <ul className="mt-8 space-y-3">
                                {RATTSSKYDD_POINTS.map((p) => (
                                    <li
                                        key={p}
                                        className="flex items-start gap-3 text-sm text-[#F4F4F5]/90"
                                    >
                                        <Check
                                            size={14}
                                            className="text-[#C4A977] mt-1 shrink-0"
                                        />
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-80px" }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            data-testid="rattshjalp-card"
                            className="bg-[#111113] border border-white/5 p-10 rounded-sm"
                        >
                            <div className="w-12 h-12 border border-[#C4A977]/40 grid place-items-center text-[#C4A977]">
                                <Landmark size={20} />
                            </div>
                            <p className="mt-6 overline">Rättshjälp</p>
                            <h2 className="mt-3 font-serif text-3xl lg:text-4xl tracking-tight leading-tight">
                                Statlig ersättning när du inte har försäkring.
                            </h2>
                            <p className="mt-5 text-[#A1A1AA] leading-relaxed">
                                Rättshjälp är ett statligt bidrag för dig som
                                inte omfattas av rättsskydd och som har begränsad
                                ekonomi. Rättshjälpen beviljas av
                                Rättshjälpsmyndigheten eller av domstol, och du
                                betalar en egen avgift som anpassas efter din
                                årsinkomst. För många är detta skillnaden mellan
                                att få driva sitt ärende eller inte.
                            </p>
                            <ul className="mt-8 space-y-3">
                                {RATTSHJALP_POINTS.map((p) => (
                                    <li
                                        key={p}
                                        className="flex items-start gap-3 text-sm text-[#F4F4F5]/90"
                                    >
                                        <Check
                                            size={14}
                                            className="text-[#C4A977] mt-1 shrink-0"
                                        />
                                        <span>{p}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    </div>
                </div>
            </section>

            <section className="py-24 bg-[#0A0A0B] border-t border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <p className="overline">Så går det till</p>
                        <h2 className="mt-4 font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            Vi sköter{" "}
                            <span className="italic text-[#C4A977]">
                                hela processen
                            </span>
                            .
                        </h2>
                        <p className="mt-6 text-[#A1A1AA] leading-relaxed">
                            Från första kontakten till att ersättningen är
                            beviljad — du behöver inte lyfta ett papper.
                        </p>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {STEG.map((s, i) => (
                            <motion.div
                                key={s.n}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.6, delay: i * 0.08 }}
                                data-testid={`rattshjalp-step-${s.n}`}
                                className="bg-[#111113] border border-white/5 p-8 rounded-sm"
                            >
                                <p className="font-serif text-4xl text-[#C4A977]/40 leading-none">
                                    {s.n}
                                </p>
                                <h3 className="mt-5 font-serif text-xl text-[#F4F4F5]">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                    {s.body}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 bg-[#0A0A0B] border-t border-white/5">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-[#111113] border border-[#C4A977]/20 p-8 lg:p-10 rounded-sm flex flex-col md:flex-row md:items-start gap-6">
                        <div className="w-12 h-12 border border-[#C4A977]/40 grid place-items-center text-[#C4A977] shrink-0">
                            <Info size={18} />
                        </div>
                        <div>
                            <h3 className="font-serif text-2xl">
                                Är du osäker på vad som gäller för dig?
                            </h3>
                            <p className="mt-3 text-[#A1A1AA] leading-relaxed">
                                Om du inte vet om ditt försäkringsbolag
                                erbjuder rättsskydd, eller om du är berättigad
                                till rättshjälp, hjälper vi dig att reda ut
                                det. Första samtalet är alltid kostnadsfritt.
                            </p>
                            <p className="mt-3 text-[#F4F4F5]/90 leading-relaxed">
                                <span className="text-[#C4A977] font-medium">
                                    Vid vårdnadstvister
                                </span>{" "}
                                är vår inledande rådgivning alltid
                                kostnadsfri — oavsett om rättsskydd eller
                                rättshjälp är aktuell.
                            </p>
                            <Link
                                to="/kontakt"
                                data-testid="rattshjalp-cta-kontakt"
                                className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-[#C4A977] text-black hover:bg-[#DBC08F] rounded-sm text-sm tracking-wide font-medium group"
                            >
                                Boka kostnadsfritt samtal
                                <ArrowRight
                                    size={14}
                                    className="transition-transform duration-300 group-hover:translate-x-1"
                                />
                            </Link>
                        </div>
                    </div>

                    <p className="mt-6 text-xs text-[#A1A1AA]/80 leading-relaxed">
                        Informationen på denna sida är av allmän karaktär och
                        utgör inte juridisk rådgivning. Vilka belopp, gränser
                        och villkor som faktiskt gäller i ditt fall avgörs av
                        ditt försäkringsbolag respektive Rättshjälpsmyndigheten.
                    </p>
                </div>
            </section>
        </div>
    );
}
